/**
 * Wallet ledger types for `sb/api/player/profile/transactions`.
 * Labels port legacy `Utilities.ActivityName` with Figma-facing English copy.
 * Do not reuse `ACTIVITY_TYPE_LABEL` (account activity page uses a different enum).
 */

export const TransactionType = {
  TicketCreate: 0,
  TicketCreateBonus: 1,
  TicketRefund: 2,
  TicketRefundBonus: 3,
  TicketWon: 4,
  TicketWonBonus: 5,
  TicketCashout: 6,
  TicketCashoutBonus: 7,
  TicketPartialCashout: 8,
  TicketPartialCashoutBonus: 9,
  TicketRollback: 10,
  TicketRollbackBonus: 11,
  ExternalGameDeposit: 12,
  ExternalGameWithdrawal: 13,
  DepositAccept: 14,
  WithdrawalCreate: 15,
  WithdrawalDecline: 16,
  ManualIn: 17,
  ManualOut: 18,
  BonusAccept: 19,
  BonusCompleted: 20,
  BonusCompletedBonusCredits: 21,
  ExternalGameDepositBonus: 22,
  ExternalGameWithdrawalBonus: 23,
  BonusCancel: 24,
  ManualCancel: 31,
} as const;

export type TransactionType = (typeof TransactionType)[keyof typeof TransactionType];

/** Display labels for wallet ledger rows (legacy ActivityName + Figma wording). */
export const LEDGER_TRANSACTION_TYPE_LABEL: Record<number, string> = {
  [TransactionType.TicketCreate]: 'New Betslip',
  [TransactionType.TicketCreateBonus]: 'New Betslip Bonus',
  [TransactionType.TicketRefund]: 'Betslip Refund',
  [TransactionType.TicketRefundBonus]: 'Betslip Refund Bonus',
  [TransactionType.TicketWon]: 'Ticket Win',
  [TransactionType.TicketWonBonus]: 'Ticket Win Bonus',
  [TransactionType.TicketCashout]: 'Cashout',
  [TransactionType.TicketCashoutBonus]: 'Cashout Bonus',
  [TransactionType.TicketPartialCashout]: 'Partial Cashout',
  [TransactionType.TicketPartialCashoutBonus]: 'Partial Cashout Bonus',
  [TransactionType.TicketRollback]: 'Betslip Rollback',
  [TransactionType.TicketRollbackBonus]: 'Betslip Rollback Bonus',
  [TransactionType.ExternalGameDeposit]: 'Casino Bet',
  [TransactionType.ExternalGameWithdrawal]: 'Casino Win',
  [TransactionType.DepositAccept]: 'Deposit Accepted',
  [TransactionType.WithdrawalCreate]: 'Withdrawal Requested',
  [TransactionType.WithdrawalDecline]: 'Withdrawal Declined',
  [TransactionType.ManualIn]: 'Manual Pay In',
  [TransactionType.ManualOut]: 'Manual Pay Out',
  [TransactionType.BonusAccept]: 'Bonus Accepted',
  [TransactionType.BonusCompleted]: 'Bonus Completed',
  [TransactionType.BonusCompletedBonusCredits]: 'Bonus Completed Bonus',
  [TransactionType.ExternalGameDepositBonus]: 'Casino Bonus Bet',
  [TransactionType.ExternalGameWithdrawalBonus]: 'Casino Bonus Win',
  [TransactionType.BonusCancel]: 'Bonus Cancelled',
  [TransactionType.ManualCancel]: 'Manual Cancel',
};

export type TransactionsCategory = 'all' | 'sports' | 'casino';

const SPORTS_TYPES = new Set<number>([
  TransactionType.TicketCreate,
  TransactionType.TicketCreateBonus,
  TransactionType.TicketRefund,
  TransactionType.TicketRefundBonus,
  TransactionType.TicketWon,
  TransactionType.TicketWonBonus,
  TransactionType.TicketCashout,
  TransactionType.TicketCashoutBonus,
  TransactionType.TicketPartialCashout,
  TransactionType.TicketPartialCashoutBonus,
  TransactionType.TicketRollback,
  TransactionType.TicketRollbackBonus,
]);

const CASINO_TYPES = new Set<number>([
  TransactionType.ExternalGameDeposit,
  TransactionType.ExternalGameWithdrawal,
  TransactionType.ExternalGameDepositBonus,
  TransactionType.ExternalGameWithdrawalBonus,
]);

export interface MachineBalanceRow {
  Id: number;
  Type: number;
  Value: number;
  Credits: number;
  BonusValue: number;
  BonusCredits: number;
  Note?: string | null;
  Date: string;
  Ticket?: string | null;
  IsBalanceActivityTable?: boolean;
}

export interface TransactionsTableFilters {
  MachineGuid: string;
  DateFrom?: string | null;
  DateTo?: string | null;
  Type?: number | null;
}

export interface TransactionsListPaging {
  PageNumber: number;
  NumberOfResultsToDisplay: number;
}

export interface TransactionsListSorting {
  SortBy: string;
  Direction: 'asc' | 'desc';
}

export interface TransactionsListRequest {
  TableFilters: TransactionsTableFilters;
  Paging: TransactionsListPaging;
  Sorting: TransactionsListSorting;
}

export interface TransactionsListPagingInfo {
  PageNumber: number;
  NumberOfPages: number;
  NumberOfResults: number;
  NumberOfResultsToDisplay?: number;
  TotalNumberOfResults?: number;
}

export interface TransactionsListResponse {
  Data: MachineBalanceRow[];
  Filter?: TransactionsListPagingInfo | null;
}

export const TRANSACTIONS_PAGE_SIZE = 50;

/** Default My Bets–style date preset (Last 7 days). */
export const TRANSACTIONS_DEFAULT_DATE_DAYS = 7;

export function ledgerTypeLabel(type: number): string {
  return LEDGER_TRANSACTION_TYPE_LABEL[type] ?? '';
}

export function matchesTransactionsCategory(
  type: number,
  category: TransactionsCategory,
): boolean {
  if (category === 'all') return true;
  if (category === 'sports') return SPORTS_TYPES.has(type);
  return CASINO_TYPES.has(type);
}

export function filterTransactionsByCategory(
  rows: readonly MachineBalanceRow[],
  category: TransactionsCategory,
): MachineBalanceRow[] {
  if (category === 'all') return [...rows];
  return rows.filter((row) => matchesTransactionsCategory(row.Type, category));
}

/**
 * Prefer ticket number as `#id`, else note; ManualCancel falls back to `#${Id}`.
 */
export function ledgerRowNote(row: MachineBalanceRow): string {
  const ticket = row.Ticket?.trim();
  if (ticket) {
    return ticket.startsWith('#') ? ticket : `#${ticket}`;
  }
  const note = row.Note?.trim();
  if (note) return note;
  if (row.Type === TransactionType.ManualCancel && row.Id != null) {
    return `#${row.Id}`;
  }
  return '';
}

/** True when cash or bonus delta is a credit (positive). */
export function isLedgerCredit(row: MachineBalanceRow): boolean {
  return (row.Value ?? 0) > 0 || (row.BonusValue ?? 0) > 0;
}

/** Format like Figma: `09 Oct 2026 · 09:49`. */
export function formatLedgerDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, '0');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = months[date.getMonth()] ?? '';
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} · ${hours}:${minutes}`;
}

/** Signed amount with optional currency prefix (`−€3.00` / `€3.00`). */
export function formatLedgerAmount(
  value: number,
  currency = '',
  decimals = 2,
): string {
  const safe = Number.isFinite(value) ? value : 0;
  const abs = Math.abs(safe).toFixed(decimals).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  const prefixed = currency ? `${currency}${abs}` : abs;
  if (safe < 0) return `−${prefixed}`;
  return prefixed;
}
